/* eslint-disable react-hooks/exhaustive-deps */
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import React from 'react'

import { decodeHashedObject, hashObject } from '@/lib/utils'
import { SearchParamKeys as SearchParamKey } from '@/types/routing.types'

type QuerySearchParam = {
  key: SearchParamKey
  value: string | Record<string, unknown>
  isHashed?: boolean
}

function useClientQueryString() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const location = useLocation()
  const pathname = location.pathname

  const append = React.useCallback(
    (queriesList: QuerySearchParam[], newPathname?: string) => {
      const params = new URLSearchParams(
        Array.from(searchParams?.entries() ?? [])
      )
      queriesList.forEach(
        ({ key, value }) =>
          typeof value === 'string' && params.append(key, value)
      )
      const searchString = params.size > 0 ? `?${params.toString()}` : ''
      navigate(`${newPathname ?? pathname}${searchString}`, {
        state: { scroll: false },
      })
    },

    [navigate, searchParams]
  )

  const set = React.useCallback(
    (queriesList: QuerySearchParam[], newPathname?: string) => {
      const params = new URLSearchParams(
        Array.from(searchParams?.entries() ?? [])
      )
      queriesList.forEach(({ key, value, isHashed }) => {
        if (typeof value === 'string' && !isHashed)
          return params.set(key, value)

        if (typeof value === 'object' && isHashed)
          return params.set(key, hashObject(value) ?? '')
      })
      const searchString = params.size > 0 ? `?${params.toString()}` : ''
      navigate(`${newPathname ?? pathname}${searchString}`, {
        state: { scroll: false },
      })
    },
    [navigate, searchParams]
  )

  const setHashed = React.useCallback(
    (
      key: SearchParamKey,
      value: Record<string, unknown>,
      newPathname?: string
    ) => {
      const params = new URLSearchParams(
        Array.from(searchParams?.entries() ?? [])
      )
      const hashedValue = hashObject(value) ?? ''
      params.set(key, hashedValue)
      const searchString = params.size > 0 ? `?${params.toString()}` : ''
      navigate(`${newPathname ?? pathname}${searchString}`, {
        state: { scroll: false },
      })
    },
    [navigate, searchParams]
  )

  const removeOneByKey = React.useCallback(
    (key: string, newPathname?: string) => {
      const params = new URLSearchParams(
        Array.from(searchParams?.entries() ?? [])
      )
      params.delete(key)
      const searchString = params.size > 0 ? `?${params.toString()}` : ''
      navigate(`${newPathname ?? pathname}${searchString}`, {
        state: { scroll: false },
      })
    },
    [navigate, searchParams]
  )

  const removeMultiple = React.useCallback(
    (keys: SearchParamKey[], newPathname?: string) => {
      const params = new URLSearchParams(
        Array.from(searchParams?.entries() ?? [])
      )
      keys.forEach((key) => {
        params.delete(key)
      })

      const searchString = params.size > 0 ? `?${params.toString()}` : ''
      navigate(`${newPathname ?? pathname}${searchString}`, {
        state: { scroll: false },
      })
    },
    [navigate, searchParams]
  )

  const getByKey = React.useCallback(
    <T = Record<string, unknown>>(
      key: SearchParamKey,
      options?: { hashed?: boolean }
    ) => {
      const { hashed } = options ?? {}

      const params = new URLSearchParams(
        Array.from(searchParams?.entries() ?? [])
      )
      const value = params.get(key) ?? ''
      return hashed ? decodeHashedObject<T>(value) : params.get(key)
    },
    [searchParams]
  )

  return {
    removeOneByKey,
    append,
    getByKey,
    set,
    setHashed,
    removeMultiple,
  }
}

export default useClientQueryString
